import React from 'react';
import { SquareChartGantt, Building, Layout, Ruler, Lightbulb } from 'lucide-react';

const RoomParametersSection = ({ 
  formData, 
  handleInputChange, 
  errors, 
  roomTypes,
  activityTypes
}) => {
  return (
    <div className="mb-6">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Building className="w-5 h-5 mr-2 text-amber-500" />
        Параметры помещения
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {/* Тип помещения */}
        <div>
          <label htmlFor="roomType" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <SquareChartGantt className="w-4 h-4 mr-2 text-amber-500" />
            Тип помещения
          </label>
          <select
            id="roomType"
            name="roomType"
            value={formData.roomType}
            onChange={handleInputChange}
            className="form-select w-full"
          >
            {roomTypes.map((type) => (
              <option key={type.id} value={type.id}>
                {type.name} ({type.defaultIlluminance} лк)
              </option>
            ))}
          </select>
          {errors.roomType && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.roomType}
            </p>
          )}
        </div>

        {/* Вид деятельности */}
        <div>
          <label htmlFor="roomActivity" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <Layout className="w-4 h-4 mr-2 text-amber-500" />
            Назначение помещения
          </label>
          <select
            id="roomActivity"
            name="roomActivity"
            value={formData.roomActivity}
            onChange={handleInputChange}
            className="form-select w-full"
          >
            {activityTypes.map((type) => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </select>
          {errors.roomActivity && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.roomActivity}
            </p>
          )}
        </div>

        {/* Размеры помещения */}
        <div>
          <label htmlFor="roomLength" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <Ruler className="w-4 h-4 mr-2 text-amber-500" />
            Длина, м
          </label>
          <input
            type="text"
            id="roomLength"
            name="roomLength"
            value={formData.roomLength}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.roomLength ? 'input-error' : ''}`}
            placeholder="Введите длину помещения"
          />
          {errors.roomLength && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.roomLength}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="roomWidth" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <Ruler className="w-4 h-4 mr-2 text-amber-500" />
            Ширина, м
          </label>
          <input
            type="text"
            id="roomWidth"
            name="roomWidth"
            value={formData.roomWidth}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.roomWidth ? 'input-error' : ''}`}
            placeholder="Введите ширину помещения"
          />
          {errors.roomWidth && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.roomWidth}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="roomHeight" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <Ruler className="w-4 h-4 mr-2 text-amber-500" />
            Высота потолка, м
          </label>
          <input
            type="text"
            id="roomHeight"
            name="roomHeight"
            value={formData.roomHeight}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.roomHeight ? 'input-error' : ''}`}
            placeholder="Введите высоту помещения"
          />
          {errors.roomHeight && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.roomHeight}
            </p>
          )}
        </div>

        {/* Целевая освещенность */}
        <div>
          <label htmlFor="targetIlluminance" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <Lightbulb className="w-4 h-4 mr-2 text-amber-500" />
            Требуемая освещенность, лк
          </label>
          <input
            type="text"
            id="targetIlluminance"
            name="targetIlluminance"
            value={formData.targetIlluminance}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.targetIlluminance ? 'input-error' : ''}`}
            placeholder="Введите требуемую освещенность"
          />
          {errors.targetIlluminance && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.targetIlluminance}
            </p>
          )}
        </div>
      </div>
      
      {/* Дополнительная информация о нормативах освещения */}
      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 border border-gray-200 dark:border-gray-600 mb-4">
        <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2 flex items-center">
          <Lightbulb className="w-4 h-4 mr-2 text-amber-500" />
          Нормативы освещенности в России
        </h4>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
          Согласно СП 52.13330.2016 и СанПиН 1.2.3685-21, нормативная освещенность зависит от назначения помещения:
        </p>
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-600 dark:text-gray-400">
          <li className="flex justify-between">
            <span>Жилые комнаты</span>
            <span className="font-medium">150-300 лк</span>
          </li>
          <li className="flex justify-between">
            <span>Кухни</span>
            <span className="font-medium">200-400 лк</span>
          </li>
          <li className="flex justify-between">
            <span>Офисные помещения</span>
            <span className="font-medium">300-500 лк</span>
          </li>
          <li className="flex justify-between">
            <span>Учебные классы</span>
            <span className="font-medium">400-500 лк</span>
          </li>
          <li className="flex justify-between">
            <span>Производственные помещения</span>
            <span className="font-medium">300-750 лк</span>
          </li>
          <li className="flex justify-between">
            <span>Санузлы, коридоры</span>
            <span className="font-medium">100-200 лк</span>
          </li>
          <li className="flex justify-between">
            <span>Торговые залы</span>
            <span className="font-medium">300-500 лк</span>
          </li>
          <li className="flex justify-between">
            <span>Медицинские помещения</span>
            <span className="font-medium">300-750 лк</span>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default RoomParametersSection;